import { h, } from "@stencil/core";
/**
 * A modal to confirm the deletion of something.
 * @part preheading - The h2 above the heading.
 * @part heading - The h1 heading.
 * @part body - The body of the modal.
 * @part warning - The warning text (if provided).
 * @part type_to_confirm - The type to confirm label (if enabled).
 * @part cancel - The cancel button.
 * @part confirm - The confirm button.
 */
export class DeleteModal {
  constructor() {
    this.clickPre = async () => {
      if (!this.typeToConfirm)
        return;
      try {
        await navigator.clipboard.writeText(this.typeToConfirm);
        if (this.copied)
          return;
        this.copied = true;
        clearTimeout(this.removeCopiedTimeout);
        this.removeCopiedTimeout = setTimeout(() => {
          this.copied = false;
        }, 1000);
      }
      catch (_error) {
        // oh well
      }
    };
    this.onInputChange = (e) => {
      const input = e.target;
      this.typed = input.value;
    };
    this.confirmDisabled = () => {
      if (!this.typeToConfirm)
        return false;
      return !!this.typeToConfirm.localeCompare(this.typed, 'en', {
        sensitivity: 'base',
      });
    };
    this.preHeading = undefined;
    this.heading = undefined;
    this.body = undefined;
    this.warning = undefined;
    this.confirm = undefined;
    this.confirmVariant = 'delete';
    this.typeToConfirm = undefined;
    this.typed = '';
    this.copied = false;
  }
  render() {
    return (h("es-modal", { role: 'alert' }, h("h2", { slot: 'header', part: 'preheading' }, this.preHeading), h("h1", { slot: 'header', part: 'heading' }, this.heading), h("p", { part: 'body' }, typeof this.body === 'string' ? this.body : h(this.body)), !!this.warning && (h("p", { class: 'important', part: 'warning' }, this.warning)), !!this.typeToConfirm && (h("label", { class: 'type_to_confirm', part: 'type_to_confirm' }, 'To confirm type ', h("pre", { onClick: this.clickPre }, this.typeToConfirm, h("es-popover", { open: this.copied }, 'Copied to clipboard')), ' in the box below.', h("input", { type: 'text', name: 'type_to_confirm', placeholder: this.typeToConfirm, onInput: this.onInputChange, value: this.typed }))), h("es-button", { variant: 'cancel', slot: 'footer', part: 'cancel', onClick: this.requestClose.emit }, 'Cancel'), h("es-button", { variant: this.confirmVariant, slot: 'footer', part: 'confirm', onClick: this.requestDeletion.emit, disabled: this.confirmDisabled() }, this.confirm)));
  }
  static get is() { return "es-confirm-modal"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["es-confirm-modal.css"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["es-confirm-modal.css"]
    };
  }
  static get properties() {
    return {
      "preHeading": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Text to display above the heading."
        },
        "attribute": "pre-heading",
        "reflect": false
      },
      "heading": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Text to display in the heading."
        },
        "attribute": "heading",
        "reflect": false
      },
      "body": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string | FunctionalComponent",
          "resolved": "FunctionalComponent<{}> | string",
          "references": {
            "FunctionalComponent": {
              "location": "import",
              "path": "@stencil/core",
              "id": "../../node_modules/@stencil/core/internal/stencil-core/index.d.ts::FunctionalComponent"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Text or component to display in the body of the modal."
        },
        "attribute": "body",
        "reflect": false
      },
      "warning": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Text to display in red below the body."
        },
        "attribute": "warning",
        "reflect": false
      },
      "confirm": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Text to display within the confirm button."
        },
        "attribute": "confirm",
        "reflect": false
      },
      "confirmVariant": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "ButtonVariant",
          "resolved": "\"cancel\" | \"default\" | \"delete\" | \"filled\" | \"link\" | \"minimal\" | \"outline\"",
          "references": {
            "ButtonVariant": {
              "location": "import",
              "path": "../../../../buttons/types",
              "id": "src/components/buttons/types.ts::ButtonVariant"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Button variant for the confirm button."
        },
        "attribute": "confirm-variant",
        "reflect": false,
        "defaultValue": "'delete'"
      },
      "typeToConfirm": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "String required to be typed to enable the confirm button."
        },
        "attribute": "type-to-confirm",
        "reflect": false
      }
    };
  }
  static get states() {
    return {
      "typed": {},
      "copied": {}
    };
  }
  static get events() {
    return [{
        "method": "requestClose",
        "name": "requestClose",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": "Triggered when the user has indicated that they want to close the modal."
        },
        "complexType": {
          "original": "any",
          "resolved": "any",
          "references": {}
        }
      }, {
        "method": "requestDeletion",
        "name": "requestDeletion",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": "Triggered when the user has indicated that they want to close the modal."
        },
        "complexType": {
          "original": "any",
          "resolved": "any",
          "references": {}
        }
      }];
  }
}
//# sourceMappingURL=es-confirm-modal.js.map
